import React,{
useState,useRef,useEffect
} from "react";
import ClientPrefill from "./ClientPrefill";
import { rememberRegistration } from "./Registrationsession";

import {
submitForm,
checkEmail
}
from "./formApi";

import "./DynamicForm.css";


/*
  WHAT CHANGED (this pass):

  1. DOCTOR MODE. `showDoctorFields` renders the fields marked
     doctorOnly in the schema instead of dropping them. Off by default,
     so the patient-facing QR flow is unchanged. SingleFormPage turns it
     on from ?scope=doctor.

     This is what makes "Additional Clinic Forms" usable: 16 of its 18
     fields are doctorOnly (Norwood staging, session tracker, treatment
     plan, billing), so a patient correctly sees only the photography
     consent, and a clinician opening the same form now sees all of it.

     SECURITY: this is a VIEW switch, not access control. The QR page is
     public, so anyone who types ?scope=doctor sees the clinician fields.
     It is fine for a clinic tablet on the desk; it is not a permission
     system. See the note in SingleFormPage.jsx.

  2. TABLE FIELDS NOW WORK. type:"table" used to render a placeholder
     saying it wasn't supported — which meant the session tracker and
     the treatment plan grid could not be filled in by anyone. There is
     now a real editor: add rows, remove rows, one input per column.
     Stored as an array of row objects, e.g.
       [{ "Date": "2026-07-31", "Session #": "3" }]

  3. Identity fields are recognised by a CANONICAL name, matching the
     server. An exact match meant a field named "Full Name" was not
     recognised as already-known, so it appeared in ClientPrefill at the
     top AND was asked again below.
*/

// Field types that should always span the full row width, even in
// the 2-column grid — anything with potentially long content or a
// list of options.
const FULL_WIDTH_TYPES = [
  "textarea",
  "paragraph",
  "radio",
  "checkbox",
  "yes_no",
  "table",
  "image",
  "file"
];


/*
  Images referenced by a schema are served by the Node app
  (app.use("/Images", express.static(...))), NOT by the React dev
  server. A patient opens this page on their phone, so a bare
  "/Images/x.png" would resolve against localhost:3000 and 404.

  Derive the host from the same env var Api.js uses, dropping the
  trailing /api.
*/
const ASSET_BASE = String(process.env.REACT_APP_QR_API_URL || "")
  .replace(/\/api\/?$/, "")
  .replace(/\/$/, "");

const resolveAssetUrl = (src) => {
  const s = String(src || "");
  if (!s) return "";
  if (/^https?:\/\//i.test(s) || s.startsWith("data:")) return s;
  return `${ASSET_BASE}${s.startsWith("/") ? "" : "/"}${s}`;
};


const canon = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

/*
  Field names that live in the `clients` table. Once the Client ID gate
  has resolved a patient these are shown read-only by ClientPrefill
  instead of being asked again.

  Stored canonical — compare with isClientField(), never directly.
  Keep in sync with CLIENT_FIELD_ALIASES in form.controller.js.
*/
const CLIENT_FIELD_KEYS = new Set([
  "name","fullname","clientname","patientname","participantname","firstname",
  "patientfullname","nameofpatient",
  "email","emailaddress","emailid","mail","mailid",
  "mobile","contactnumber","phone","phonenumber","mobilenumber","phonemobile",
  "mobileno","phoneno","contactno","contact","cell","cellnumber","whatsappnumber",
  "address","clientaddress","homeaddress","residentialaddress",
  "gender","sex",
  "dob","dateofbirth","birthdate",
  "bloodgroup","bloodtype",
  "password"
]);

const isClientField = (name) => CLIENT_FIELD_KEYS.has(canon(name));

/*
  Category wording — see formCategories.js. SingleFormPage passes this
  in; the default keeps the component usable on its own.
*/

/*
====================================================================
MOBILE RULES

Injected from here rather than added to DynamicForm.css, for the same
reason FormPortal carries its own styles: this component's layout must
not depend on a stylesheet edit landing separately.

Everything is scoped under .df-page, so nothing leaks.

WHAT IT FIXES
  - the field grid staying two columns on a 360px screen, leaving
    ~150px per field — not enough for "Emergency Contact (Name & Number)"
  - a long email or a 60-character option pushing the card wider than
    the screen
  - a wide table scrolling the PAGE sideways instead of scrolling itself
  - inputs under 16px, which makes iOS Safari zoom on focus and stay
    zoomed, putting every following field off screen
====================================================================
*/
const MOBILE_CSS = `
/*
  ONLY .df-scroll APPLIES AT EVERY WIDTH, and it is a class that did not
  exist before, so it cannot change anything that already looked right.
  A wide table has to scroll itself on a laptop too, or it stretches the
  card.
*/
.df-page .df-scroll {
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

/* --------------------------------------------------------------
   EVERYTHING BELOW IS PHONE-ONLY.

   The first version of this block put box-sizing, overflow-wrap and
   overflow-x outside the media query, so they applied on desktop as
   well and shifted layouts that were already correct. Desktop and
   laptop rendering must be byte-identical to before; only widths under
   640px change.
   -------------------------------------------------------------- */

@media (max-width: 640px) {
  .df-page, .df-page * { box-sizing: border-box; }

  /* One stray overflow scrolls the whole page sideways, which reads as
     broken rather than as a wide table. */
  .df-page { overflow-x: hidden; }
  .df-page .df-card { max-width: 100%; }

  /* Long unbroken strings — an email, a URL, a run-on option label. */
  .df-page .df-label,
  .df-page .df-paragraph,
  .df-page .df-option-row,
  .df-page .df-input,
  .df-page .df-textarea { overflow-wrap: anywhere; }

  /* Two 50% fields on a 360px screen leave ~150px each, which is not
     enough for "Emergency Contact (Name & Number)". */
  .df-page .df-field-grid { display: block; }
  .df-page .df-field { width: 100% !important; margin-bottom: 14px; }

  .df-page .df-card { border-radius: 12px; }
  .df-page .df-header { padding: 16px 14px; }
  .df-page .df-body { padding: 14px; }

  /* 16px minimum: below that iOS Safari zooms the page on focus and
     does not zoom back, so every following field lands off screen. */
  .df-page .df-input,
  .df-page .df-textarea,
  .df-page .df-select { font-size: 16px; }

  .df-page .df-submit-bar { padding: 12px 14px; }
  .df-page .df-submit-btn { width: 100%; }
}

@media (max-width: 380px) {
  .df-page .df-section-title { font-size: 15px; }
  .df-page .df-header h2 { font-size: 18px; }
}
`;


const DEFAULT_TERMS = {
  person: "client",
  Person: "client",
  idLabel: "Client ID",
  detailsTitle: "Client Details"
};

// Which of those fields is the email — the one the live check watches.
const EMAIL_FIELD_KEYS = new Set([
  "email","emailaddress","emailid","mail","mailid"
]);

const isEmailField = (name) => EMAIL_FIELD_KEYS.has(canon(name));


/*
  OPTION LAYOUT

  Short options belong on one line — Male / Female / Other, or
  Call / WhatsApp / Email / SMS, which is how they are printed on the
  paper form. Stacking three one-word choices down the page wastes a
  whole screen on a phone and reads nothing like the original.

  Long options still stack: the Fitzpatrick descriptions and the consent
  acknowledgements are full sentences, and side by side they would wrap
  into an unreadable mess.

  24 characters is the cutoff — comfortably past "Prefer not to say" and
  "Yes, I Consent", comfortably short of any sentence.
*/
/*
  OPTION LAYOUT — always a row, never a column.

  Every option group is a wrapping flex row. Short choices sit side by
  side the way they are printed: Male / Female / Other, or
  I / II / III / IV / V / VI across one line instead of six.

  Long options still get a line each, but by WRAPPING rather than by a
  separate stacked layout — a consent acknowledgement is a full
  sentence, so it fills the row and the next one starts below it. One
  system, two outcomes, and no threshold that makes a group of
  medium-length options behave unpredictably.

  The number below only decides whether an option may break mid-item:
  short ones keep their words together, long ones wrap internally like
  ordinary text.
*/
const INLINE_OPTION_MAX = 24;


/*
  CONDITIONAL FIELDS  —  field.showIf

    showIf: { field: "gender", equals: "Male" }
    showIf: { field: "gender", in: ["Male", "Other"] }

  Added for the Hair form: the Hamilton-Norwood chart is a male pattern
  baldness scale and the Ludwig chart is the female one, so showing both
  to everyone is wrong twice over — it asks a woman to stage herself
  against male photographs, and doubles the length of the page.

  Comparison is case-insensitive because the same value arrives as
  "Male" from a radio and "male" from the clients ENUM.

  A field with no showIf always renders, so nothing else changes.
*/
const sameValue = (a, b) =>
  String(a ?? "").trim().toLowerCase() === String(b ?? "").trim().toLowerCase();

function passesShowIf(field, data, client) {
  const rule = field.showIf;
  if (!rule || !rule.field) return true;

  // The answer on this form wins; fall back to the clients row, so a
  // returning patient whose gender is already known doesn't have to
  // re-answer before the right chart appears.
  const current =
    data[rule.field] !== undefined && data[rule.field] !== ""
      ? data[rule.field]
      : client
        ? client[rule.field]
        : undefined;

  if (Array.isArray(rule.in)) return rule.in.some((v) => sameValue(v, current));
  if (rule.equals !== undefined) return sameValue(rule.equals, current);
  if (rule.notEmpty) return String(current ?? "").trim() !== "";

  return true;
}

// Short enough to keep on one unbroken line.
const optionsAreShort = (options) =>
  Array.isArray(options) &&
  options.length > 0 &&
  options.every((o) => String(o).length <= INLINE_OPTION_MAX);

// Inline styles rather than a new class: DynamicForm.css already styles
// .df-option-group / .df-option-row as stacked blocks, and an inline
// style wins without needing that file edited in step.
/*
  A GRID, NOT A FLEX ROW.

  Two mistakes are fixed here. DynamicForm.css sets flex-direction:
  column on .df-option-group; the inline style set display:flex but
  never overrode the direction, so every option still stacked — and
  alignItems:center then centred each one horizontally, which is the
  drifting, indented column that appeared on screen.

  Grid also matches the printed page better than a flowing row: the
  paper form lays Medical Conditions out in two neat columns, and
  auto-fill does the same at whatever width the phone or desktop gives
  it. Items line up under each other instead of stepping in and out as
  each label's length changes.

  minmax() takes the column count from the longest option: short
  choices like Male / Female / Other pack onto one line, twenty medical
  conditions fall into two or three tidy columns.
*/
const optionGrid = (short) => ({
  display: "grid",
  /*
    min() caps the column at the viewport. A bare minmax(240px, 1fr) on
    a 320px phone leaves no room for the card's own padding, so the grid
    overflows by a few pixels — enough to make the whole page scroll
    sideways for no visible reason.
  */
  gridTemplateColumns: short
    ? "repeat(auto-fill, minmax(min(120px, 100%), max-content))"
    : "repeat(auto-fill, minmax(min(240px, 100%), 1fr))",
  gap: "10px 24px",
  justifyContent: "start",
  alignItems: "start",
  width: "100%",
  margin: "4px 0 0"
});

/* One option: box on the left, label beside it, both top-aligned so a
   label that wraps to two lines keeps its box against the first. */
const optionItem = (short) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: 8,
  width: "auto",
  margin: 0,
  padding: 0,
  border: "none",
  background: "transparent",
  textAlign: "left",
  whiteSpace: short ? "nowrap" : "normal",
  lineHeight: 1.45,
  cursor: "pointer"
});


/*
  `client` and `qrToken` are passed in by SingleFormPage:
    - registration  -> client is null, the patient fills everything
    - anything else -> the Client ID gate resolved the client first
*/
const DynamicForm=({
  form,
  client=null,
  qrToken=null,
  terms=DEFAULT_TERMS,
  showDoctorFields=false,
  onSubmitted
}) => {


const [data,setData]=useState({});

const [submitting,setSubmitting]=useState(false);

/*
  LIVE EMAIL CHECK (registration forms only)

  status: null = not checked, "checking", "free", "taken", "invalid"

  The patient learns the address is already registered while they are
  still looking at the email box, instead of after filling three pages
  and pressing Submit. The server refuses the duplicate either way —
  this is only about when the patient finds out.
*/
const [emailStatus,setEmailStatus]=useState(null);
const [emailMessage,setEmailMessage]=useState("");

/*
  Set after a registration that CREATED a client. Holds the new Client
  ID so it can be shown before the patient leaves the page — until now
  the number went out by email only, and a patient without their phone
  had no way to open the next form.
*/
const [issued,setIssued]=useState(null);

const emailTimer = useRef(null);
const emailSeq = useRef(0);


const runEmailCheck=(value)=>{

const email = String(value || "").trim();

if(!email){
  setEmailStatus(null);
  setEmailMessage("");
  return;
}

setEmailStatus("checking");
setEmailMessage("");

// Ignore a slow reply that lands after a newer keystroke's reply.
const seq = ++emailSeq.current;

checkEmail(qrToken, email)
  .then((res)=>{

    if(seq !== emailSeq.current) return;

    const { available, reason, message } = res.data || {};

    if(available === false){
      setEmailStatus(reason === "invalid" ? "invalid" : "taken");
      setEmailMessage(message || "");
    } else if(available === true){
      setEmailStatus("free");
      setEmailMessage("");
    } else {
      setEmailStatus(null);
      setEmailMessage("");
    }

  })
  .catch(()=>{
    // Never block typing on a failed check — submit is the real gate.
    if(seq !== emailSeq.current) return;
    setEmailStatus(null);
    setEmailMessage("");
  });

};


// Simple fields: text, number, date, textarea, select, radio, yes_no, signature
const handleChange=(e)=>{


const {

name,

value

}=e.target;



setData(prev=>({

...prev,

[name]: value

}));


/*
  Only on a registration form: `client` is null there, and it is the
  only form that creates a clients row. On other forms the email is
  already known and isn't asked.
*/
if(!client && isEmailField(name)){

  setEmailStatus(null);
  setEmailMessage("");

  clearTimeout(emailTimer.current);
  emailTimer.current = setTimeout(()=> runEmailCheck(value), 600);

}


};


// Checkbox GROUP: toggles one option in/out of an array for that field name
const handleCheckboxGroupChange=(fieldName,option,checked)=>{

setData(prev=>{

const existing = Array.isArray(prev[fieldName]) ? prev[fieldName] : [];

const updated = checked
  ? [...existing, option]
  : existing.filter(o => o !== option);

return {
  ...prev,
  [fieldName]: updated
};

});

};


// Legacy single boolean checkbox (no options array on the field)
const handleSingleCheckboxChange=(fieldName,checked)=>{

setData(prev=>({

...prev,

[fieldName]: checked

}));

};


/*
  TABLE FIELD
  Rows are plain objects keyed by column label, so a submission reads
  back sensibly on its own without needing the schema alongside it.
*/
const tableRows = (fieldName) =>
  Array.isArray(data[fieldName]) ? data[fieldName] : [];

const handleTableChange=(fieldName,rowIdx,column,value)=>{

setData(prev=>{

const rows = (Array.isArray(prev[fieldName]) ? prev[fieldName] : [])
  .map(r => ({ ...r }));

while(rows.length <= rowIdx){
  rows.push({});
}

rows[rowIdx][column] = value;

return { ...prev, [fieldName]: rows };

});

};

const addTableRow=(fieldName)=>{

setData(prev=>({
  ...prev,
  [fieldName]: [
    ...(Array.isArray(prev[fieldName]) ? prev[fieldName] : []),
    {}
  ]
}));

};

const removeTableRow=(fieldName,rowIdx)=>{

setData(prev=>({
  ...prev,
  [fieldName]: (Array.isArray(prev[fieldName]) ? prev[fieldName] : [])
    .filter((_,i)=> i !== rowIdx)
}));

};




// Don't leave a pending check firing into an unmounted component.
useEffect(()=>()=> clearTimeout(emailTimer.current), []);


const handleSubmit=async()=>{


if(submitting) return;


// The server refuses it anyway; stopping here saves the patient a
// round trip and keeps the message next to the field it belongs to.
if(emailStatus === "taken" || emailStatus === "invalid"){
  alert(emailMessage || "Please correct the email address before submitting.");
  return;
}


try{

setSubmitting(true);


// admin_id is NOT sent — the server derives it from qr_token, so a
// patient can't file a submission into another clinic's records.
const res = await submitForm({

qr_token: qrToken,

form_id: form.id,

client_id: client ? client.id : null,

data: data

});


setData({});


/*
  A brand-new client. Show the ID rather than navigating away: it is
  the one thing the patient needs to fill in anything else, and the
  registration email may not arrive for minutes — or at all, if they
  mistyped the address.

  It is also remembered for this tab, so the Client ID gate on the next
  form opens with the number already in it.
*/
const newId = res?.data?.client_id;

if(!client && res?.data?.new_client && newId){

  rememberRegistration({
    clientId: newId,
    name: data.full_name || data.name || "",
    email: data.email || "",
    token: qrToken
  });

  setIssued({
    id: newId,
    email: data.email || "",
    name: data.full_name || data.name || ""
  });

  return;

}


alert("Submitted Successfully");


if(onSubmitted){

  onSubmitted();

}



}
catch(err){

console.log(err);

// Show the server's real message ("missing required patient details",
// "That email is already registered", ...) instead of a bare failure.
alert(
  err.response?.data?.message || "Submission failed"
);


}
finally{

setSubmitting(false);

}



};



// Renders a single field's INPUT ONLY (label is rendered by the caller).
const renderField=(field)=>{


// Belt and braces: the section filter already removes these, but a
// doctorOnly field must never render in the patient view.
if(field.doctorOnly && !showDoctorFields){

return null;

}


switch(field.type){


case "paragraph": {

  /*
    Same fix as the PDF: a consent clause has a bold heading over its
    text — "Purpose of Treatment", "Medical Disclosure" — and rendering
    only the description dropped every one of them, so eight separate
    clauses read as one wall of prose.

    " · " is treated as a list separator, matching how the risk lists
    are written into the schema.
  */
  /*
    An empty label means "no heading, just the text". Falling back to
    the field name printed "fee_note" as a bold heading over the note
    it belonged to.
  */
  const text = field.description || "";
  const raw = String(field.label || "").trim();
  const heading = field.description && raw ? raw : null;
  const items = text.includes(" · ") ? text.split(" · ") : null;

  return(
    <div style={{marginBottom:14}}>
      {heading && (
        <div style={{fontWeight:700, fontSize:14, marginBottom:4}}>
          {heading}
        </div>
      )}
      {items ? (
        <ul style={{margin:"4px 0 0 18px", padding:0}}>
          {items.map((t,i)=>(
            <li key={i} style={{marginBottom:3, lineHeight:1.5}}>
              {t.replace(/\.$/, "").trim()}
            </li>
          ))}
        </ul>
      ) : (
        <p className="df-paragraph" style={{margin:0}}>
          {text || field.label}
        </p>
      )}
    </div>
  );

}


case "textarea":

  return(
    <textarea
      className="df-textarea"
      name={field.name}
      placeholder={field.placeholder || ""}
      value={data[field.name] || ""}
      onChange={handleChange}
    />
  );


case "number":

  return(
    <input
      className="df-input"
      type="number"
      name={field.name}
      placeholder={field.placeholder || ""}
      value={data[field.name] || ""}
      onChange={handleChange}
    />
  );


case "date":

  return(
    <input
      className="df-input"
      type="date"
      name={field.name}
      value={data[field.name] || ""}
      onChange={handleChange}
    />
  );


case "radio": {

  const short = optionsAreShort(field.options);

  return(
    <div className="df-option-group" style={optionGrid(short)}>
      {
        (field.options || []).map((option,idx)=>(
          <label
            key={idx}
            className="df-option-row"
            style={optionItem(short)}
          >
            <input
              type="radio"
              name={field.name}
              value={option}
              checked={data[field.name] === option}
              onChange={handleChange}
            />
            {option}
          </label>
        ))
      }
    </div>
  );

}


case "yes_no":

  // Always one line — it is two words.
  return(
    <div className="df-yesno-row" style={optionGrid(true)}>
      <label className="df-option-row" style={optionItem(true)}>
        <input
          type="radio"
          name={field.name}
          value="Yes"
          checked={data[field.name] === "Yes"}
          onChange={handleChange}
        />
        Yes
      </label>
      <label className="df-option-row" style={optionItem(true)}>
        <input
          type="radio"
          name={field.name}
          value="No"
          checked={data[field.name] === "No"}
          onChange={handleChange}
        />
        No
      </label>
    </div>
  );


case "select":

  return(
    <select
      className="df-select"
      name={field.name}
      value={data[field.name] || ""}
      onChange={handleChange}
    >
      <option value="" disabled>Select…</option>
      {
        (field.options || []).map((option,idx)=>(
          <option key={idx} value={option}>{option}</option>
        ))
      }
    </select>
  );


case "checkbox":

  if(Array.isArray(field.options) && field.options.length > 0){

    const selected = Array.isArray(data[field.name]) ? data[field.name] : [];
    const short = optionsAreShort(field.options);

    return(
      <div className="df-option-group" style={optionGrid(short)}>
        {
          field.options.map((option,idx)=>(
            <label
              key={idx}
              className="df-option-row"
              style={optionItem(short)}
            >
              <input
                type="checkbox"
                checked={selected.includes(option)}
                onChange={(e)=>
                  handleCheckboxGroupChange(field.name,option,e.target.checked)
                }
              />
              {option}
            </label>
          ))
        }
      </div>
    );

  }

  return(
    <label className="df-option-row" style={{display:"inline-flex",width:"auto"}}>
      <input
        type="checkbox"
        checked={!!data[field.name]}
        onChange={(e)=>handleSingleCheckboxChange(field.name,e.target.checked)}
      />
      {field.label}
    </label>
  );


case "signature":

  return(
    <input
      className="df-input"
      type="text"
      name={field.name}
      placeholder="Type your full name to sign"
      value={data[field.name] || ""}
      onChange={handleChange}
    />
  );


case "file":

  return(
    <input
      className="df-input"
      type="file"
      name={field.name}
      disabled
    />
  );


/*
  IMAGE — a reference picture printed on the paper form (the
  Hamilton-Norwood chart, a Fitzpatrick table, etc). Not an input:
  nothing is collected, it is there to be looked at while answering
  the field next to it.

  `src` may be absolute ("https://...") or server-relative
  ("/Images/norwood-scale.png"), in which case it resolves against the
  API host — the QR page is opened on a phone, so it must not assume
  the file sits on the React dev server.
*/
case "image":

  return(
    <figure style={{margin:0}}>
      <img
        src={resolveAssetUrl(field.src)}
        alt={field.alt || field.label || "Reference chart"}
        style={{
          display:"block",
          width:"100%",
          maxWidth: field.maxWidth || 720,
          height:"auto",
          border:"1px solid #D8DDEB",
          borderRadius:8
        }}
      />
      {field.caption && (
        <figcaption style={{color:"#7c8a8d", fontSize:12, marginTop:6}}>
          {field.caption}
        </figcaption>
      )}
    </figure>
  );


/*
  REFERENCE_TABLE — printed on the form to be read, not filled in.
  The Fitzpatrick page is a lookup table the patient consults before
  answering the question next to it; type "table" would render it as an
  empty editable grid, which is the opposite of the point.
*/
case "reference_table": {

  const columns = Array.isArray(field.columns) ? field.columns : [];
  const rows = Array.isArray(field.rows) ? field.rows : [];

  if (!columns.length || !rows.length) return null;

  return (
    /*
      maxWidth:100% is the part that matters. Without it the table's
      minWidth pushes this div wider than the viewport, the whole page
      scrolls sideways, and the section headers drift off screen. With
      it, only the table scrolls and the rest of the form stays put.
    */
    <div className="df-scroll" style={{overflowX:"auto", maxWidth:"100%"}}>
      <table
        style={{
          width:"100%",
          borderCollapse:"collapse",
          fontSize:13.5,
          minWidth: columns.length * 120,
          // honour colWidths when the schema sets them
          tableLayout: Array.isArray(field.colWidths) ? "fixed" : "auto"
        }}
      >
        {Array.isArray(field.colWidths) && (
          <colgroup>
            {field.colWidths.map((w,i)=><col key={i} style={{width:w}} />)}
          </colgroup>
        )}
        <thead>
          <tr>
            {columns.map((c,i)=>(
              <th key={i} style={{
                textAlign:"left", padding:"8px 10px",
                background:"#172056", color:"#fff", fontWeight:600
              }}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r,ri)=>(
            <tr key={ri}>
              {r.map((cell,ci)=>(
                <td key={ci} style={{
                  border:"1px solid #D8DDEB", padding:"7px 10px",
                  background: ri % 2 ? "#FAFBFD" : "#fff"
                }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

}


case "table": {

  const columns = Array.isArray(field.columns) ? field.columns : [];

  if(columns.length === 0){
    return(
      <p className="df-placeholder-note">
        (This table has no columns defined in the form schema.)
      </p>
    );
  }

  const rows = tableRows(field.name);

  return(
    <div>

      <div className="df-scroll" style={{overflowX:"auto", maxWidth:"100%"}}>
        <table
          style={{
            width:"100%",
            borderCollapse:"collapse",
            fontSize:14,
            minWidth: columns.length * 130
          }}
        >
          <thead>
            <tr>
              {columns.map((col,i)=>(
                <th
                  key={i}
                  style={{
                    textAlign:"left",
                    padding:"8px 10px",
                    background:"#172056",
                    color:"#fff",
                    fontWeight:600,
                    whiteSpace:"nowrap"
                  }}
                >
                  {col}
                </th>
              ))}
              <th style={{width:44, background:"#172056"}} />
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length + 1}
                  style={{padding:"14px 10px", color:"#7c8a8d"}}
                >
                  No rows yet — press “Add row” to start.
                </td>
              </tr>
            )}

            {rows.map((row,rowIdx)=>(
              <tr key={rowIdx}>
                {columns.map((col,colIdx)=>(
                  <td
                    key={colIdx}
                    style={{border:"1px solid #D8DDEB", padding:4}}
                  >
                    <input
                      className="df-input"
                      style={{border:"none", padding:"6px 8px"}}
                      value={row[col] || ""}
                      onChange={(e)=>
                        handleTableChange(field.name,rowIdx,col,e.target.value)
                      }
                    />
                  </td>
                ))}
                <td
                  style={{
                    border:"1px solid #D8DDEB",
                    padding:4,
                    textAlign:"center"
                  }}
                >
                  <button
                    type="button"
                    onClick={()=>removeTableRow(field.name,rowIdx)}
                    title="Remove this row"
                    style={{
                      border:"none",
                      background:"transparent",
                      color:"#c0392b",
                      fontSize:18,
                      cursor:"pointer",
                      lineHeight:1
                    }}
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button
        type="button"
        onClick={()=>addTableRow(field.name)}
        style={{
          marginTop:10,
          padding:"8px 16px",
          border:"1px solid #172056",
          background:"#fff",
          color:"#172056",
          borderRadius:6,
          cursor:"pointer",
          fontWeight:600
        }}
      >
        + Add row
      </button>

    </div>
  );

}


case "text":
default:

  return(
    <input
      className="df-input"
      type="text"
      name={field.name}
      placeholder={field.placeholder || ""}
      value={data[field.name] || ""}
      onChange={handleChange}
    />
  );


}


};


/*
  A field is rendered when:
    - it isn't doctorOnly, OR we're in doctor mode, AND
    - it isn't something we already know from the clients row.
*/
const shouldAsk = (field) =>
  (showDoctorFields || !field.doctorOnly) &&
  !(client && isClientField(field.name)) &&
  passesShowIf(field, data, client);



/*
  REGISTRATION COMPLETE

  A full screen, not a dismissable dialog. A patient who taps outside a
  modal to close it would lose the only on-screen copy of their Client
  ID, and the email they were told to check may be minutes away or
  wrong. The one way out is the button, which carries them back to the
  form list with the ID already remembered for this tab.
*/
if(issued){

return(

<div className="df-page">
<style>{MOBILE_CSS}</style>
<div className="df-card">

  <div className="df-header">
    <h2>You&rsquo;re registered</h2>
    <p>Please note down the number below before you continue.</p>
  </div>

  <div className="df-body">
    <div className="df-section" style={{textAlign:"center"}}>

      <span className="df-label" style={{justifyContent:"center"}}>
        Your {terms.idLabel}
      </span>

      <div style={{
        fontSize:38,
        fontWeight:800,
        letterSpacing:3,
        color:"#172056",
        margin:"6px 0 4px",
        wordBreak:"break-all"
      }}>
        {issued.id}
      </div>

      {issued.name && (
        <div style={{fontSize:15, fontWeight:600, color:"#14172E"}}>
          {issued.name}
        </div>
      )}

      {issued.email && (
        <p style={{color:"#71858a", fontSize:13.5, marginTop:14, lineHeight:1.55}}>
          We&rsquo;ve emailed it to <b style={{color:"#14172E"}}>{issued.email}</b>{" "}
          along with your login details. If it doesn&rsquo;t arrive, check
          your spam folder or ask at reception &mdash; they can look it up
          for you.
        </p>
      )}

      <div style={{
        background:"#F1F3F8",
        border:"1px solid #CDD3E5",
        borderRadius:10,
        padding:"14px 16px",
        marginTop:18,
        textAlign:"left"
      }}>
        <div style={{fontWeight:700, marginBottom:4, color:"#172056"}}>
          Filling in another form?
        </div>
        <div style={{fontSize:13.5, color:"#71858a", lineHeight:1.55}}>
          You&rsquo;ll be asked for this {terms.idLabel}. We&rsquo;ve already
          filled it in for you on this device &mdash; just press Continue.
        </div>
      </div>

    </div>
  </div>

  <div className="df-submit-bar">
    <button
      className="df-submit-btn"
      onClick={()=>{ setIssued(null); if(onSubmitted) onSubmitted(); }}
    >
      Continue to forms
    </button>
  </div>

</div>
</div>

);

}


const sections = Array.isArray(form.sections) ? form.sections : [];

if(sections.length === 0){

return(

<div className="df-page">
  <style>{MOBILE_CSS}</style>
  <div className="df-card">
    <div className="df-empty">
      <h2>{form.form_name || "Form"}</h2>
      <p>This form has no fields configured, or the field data is invalid.</p>
    </div>
  </div>
</div>

);

}


// Sections left with nothing to show are dropped, so a section whose
// fields are all doctorOnly doesn't render as an empty numbered heading
// on a patient's phone.
const visibleSections = sections.filter(section =>
  (section.fields || []).some(shouldAsk)
);


const hiddenSectionCount = sections.length - visibleSections.length;



return(

<div className="df-page">
<style>{MOBILE_CSS}</style>
<div className="df-card">


<div className="df-header">
  <h2>{form.form_name}</h2>
  {form.description && <p>{form.description}</p>}
  {showDoctorFields && (
    <p style={{marginTop:6, fontWeight:600}}>
      Clinician view — includes sections not shown to clients
    </p>
  )}
</div>


<div className="df-body">

{client && (
  <div className="df-section">
    <ClientPrefill client={client} terms={terms} />
  </div>
)}


{

visibleSections.length === 0 && client && (
  <div className="df-section">
    <p className="df-placeholder-note">
      Nothing further to fill in — press Submit to record this form.
    </p>
  </div>
)

}


{

visibleSections
.map((section,sectionIdx)=>(

<div key={sectionIdx} className="df-section">

<div className="df-section-header">
  <span className="df-section-badge">{sectionIdx + 1}</span>
  <h3 className="df-section-title">{section.title}</h3>
</div>

{section.description && (
  <p style={{color:"#7c8a8d", fontSize:13, marginTop:-4, marginBottom:12}}>
    {section.description}
  </p>
)}


<div className="df-field-grid">

{

(section.fields || [])

.filter(shouldAsk)

.map((field,fieldIdx)=>{

const isFullWidth =
  FULL_WIDTH_TYPES.includes(field.type) ||
  (field.type === "checkbox" && !(field.options && field.options.length));

return(

<div
  key={field.name || fieldIdx}
  className={
    isFullWidth
      ? "df-field df-field-full"
      : "df-field"
  }
>

{

field.type !== "paragraph" &&
!(field.type === "checkbox" && !(field.options && field.options.length)) &&

<span className="df-label">
  {field.label}
  {field.required && <span className="df-required">*</span>}
  {field.doctorOnly && (
    <span
      style={{
        marginLeft:8,
        fontSize:11,
        fontWeight:600,
        color:"#172056",
        background:"#E3E6F2",
        border:"1px solid #CDD3E5",
        borderRadius:4,
        padding:"1px 6px",
        verticalAlign:"middle"
      }}
    >
      DOCTOR
    </span>
  )}
</span>

}

{field.description && field.type !== "paragraph" && (
  <span style={{color:"#7c8a8d", fontSize:12, marginBottom:6}}>
    {field.description}
  </span>
)}


{renderField(field)}


{!client && isEmailField(field.name) && (
  <>
    {emailStatus === "checking" && (
      <span style={{color:"#7c8a8d", fontSize:12, marginTop:6}}>
        Checking…
      </span>
    )}
    {(emailStatus === "taken" || emailStatus === "invalid") && (
      <span style={{color:"#c0392b", fontSize:13, marginTop:6}}>
        {emailMessage}
      </span>
    )}
    {emailStatus === "free" && (
      /*
        Neutral ink, not the brand maroon. The "already registered"
        message directly above is red, and a maroon confirmation sits
        close enough to it that a patient glancing down would not know
        which they were looking at. The tick carries the meaning; the
        colour only needs to stay out of the way.
      */
      <span style={{color:"#5A5A5A", fontSize:12, marginTop:6}}>
        ✓ Available
      </span>
    )}
  </>
)}


</div>

);

})

}


</div>


</div>

))

}

</div>


<div className="df-submit-bar">
  {!showDoctorFields && hiddenSectionCount > 0 && (
    <span style={{color:"#7c8a8d", fontSize:12, marginRight:"auto"}}>
      {hiddenSectionCount} section{hiddenSectionCount > 1 ? "s" : ""} completed by the clinic
    </span>
  )}
  <button
    className="df-submit-btn"
    onClick={handleSubmit}
    disabled={
      submitting ||
      emailStatus === "taken" ||
      emailStatus === "invalid"
    }
  >
    {submitting ? "Submitting…" : "Submit"}
  </button>
</div>


</div>
</div>


);


};


export default DynamicForm;